import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UtilityService } from '../../service/utility.service';
import { ConfigService } from '../../service/config.service';
import { LandingFeature } from '../../models/PortalConfigurationView';
import {
  PortalFeatureDirective,
  PortalLoginRequiredDirective,
  PortalButtonDirective,
} from '../../directives';

interface Slide {
  image: string;
  alt: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PortalFeatureDirective,
    PortalLoginRequiredDirective,
    PortalButtonDirective,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
})
export class HomeComponent implements OnInit, OnDestroy {
  constructor(
    private utilityService: UtilityService,
    private configService: ConfigService,
  ) {}

  LoggedIn = JSON.parse(localStorage.getItem('login') || 'false');
  slides: Slide[] = [];
  currentSlide = 0;
  /** Time each hero image stays on screen before the carousel advances. */
  readonly slideIntervalMs = 4000;
  private configSub?: Subscription;
  private slideTimer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.refreshSlides();
    this.configSub = this.configService.getConfig$().subscribe(() => {
      this.refreshSlides();
    });
  }

  ngOnDestroy(): void {
    if (this.configSub) this.configSub.unsubscribe();
    if (this.slideTimer) clearInterval(this.slideTimer);
  }

  private refreshSlides(): void {
    this.slides = this.resolveSlides();
    this.currentSlide = 0;
    this.restartAutoSlide();
  }

  private resolveSlides(): Slide[] {
    const json = this.utilityService.portalConfig?.portalHeroImages;
    if (!json) return [];
    try {
      const parsed = JSON.parse(json);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((item: any) => item && typeof item.url === 'string' && item.url)
          .map((item: any) => ({
            image: item.url,
            alt: item.fileName || 'Hero image',
          }));
      }
    } catch {
      return [];
    }
    return [];
  }

  /**
   * Every slide stays mounted and only `currentSlide` changes; CSS cross-fades
   * the `.active` image. There is no second "next" layer whose src is swapped
   * mid-transition, so the indicator, the visible image and the timer can no
   * longer disagree, and an image that is still downloading never flashes
   * through as a blank frame.
   */
  nextSlide(): void {
    if (this.slides.length < 2) return;
    this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    this.restartAutoSlide();
  }

  prevSlide(): void {
    if (this.slides.length < 2) return;
    this.currentSlide =
      (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.restartAutoSlide();
  }

  goToSlide(index: number): void {
    if (index < 0 || index >= this.slides.length || index === this.currentSlide) return;
    this.currentSlide = index;
    this.restartAutoSlide();
  }

  trackBySlide(_index: number, slide: Slide): string {
    return slide.image;
  }

  /** Stops any running timer and starts a fresh one, so a manual navigation
   *  gets a full interval before the next automatic advance. */
  private restartAutoSlide(): void {
    if (this.slideTimer) {
      clearInterval(this.slideTimer);
      this.slideTimer = undefined;
    }
    if (this.slides.length > 1) {
      this.slideTimer = setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      }, this.slideIntervalMs);
    }
  }

  get welcomeParagraphs(): string[] {
    const content = this.utilityService.portalConfig?.portalLandingContent;
    if (content && content.trim()) {
      return content
        .split(/\r?\n/)
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
    }
    return [];
  }

  isFeatureVisible(index: 0 | 1 | 2): boolean {
    const features = this.landingFeatures;
    if (!features[index]?.enabled) return false;

    const cfg = this.utilityService.portalConfig;
    if (!cfg) return true;

    const flag =
      index === 0
        ? cfg.registrationFeatureEnabled
        : index === 1
          ? cfg.signupAndProfilingEnabled
          : cfg.invoiceFeatureEnabled;
    return String(flag).toLowerCase() !== 'false';
  }

  get landingFeatures(): LandingFeature[] {
    const empty: LandingFeature[] = [
      { enabled: false, title: '', content: '', buttonText: '' },
      { enabled: false, title: '', content: '', buttonText: '' },
      { enabled: false, title: '', content: '', buttonText: '' },
    ];
    const json = this.utilityService.portalConfig?.landingFeatures;
    if (!json) return empty;
    try {
      const parsed = JSON.parse(json);
      if (!Array.isArray(parsed) || parsed.length !== 3) return empty;
      return parsed.map((f: any) => ({
        enabled: typeof f?.enabled === 'boolean' ? f.enabled : false,
        title: f?.title || '',
        content: f?.content || '',
        buttonText: f?.buttonText || '',
      }));
    } catch {
      return empty;
    }
  }

  // Direct access to portal configuration
  get portalConfig() {
    return this.utilityService.portalConfig;
  }
}
